// ============================================
// PHYSICIAN SPECIALTIES
// ============================================

export interface Specialty {
    id: string;
    name: string;
    category: string;
    // Template IDs to prioritize for this specialty (in order)
    priorityTemplates: string[];
  }
  
  export const SPECIALTY_CATEGORIES = [
    "Primary Care",
    "Internal Medicine Subspecialties",
    "Surgical Specialties",
    "Pediatric Specialties",
    "Other Specialties",
  ] as const;
  
  export const SPECIALTIES: Specialty[] = [
    // ==========================================
    // PRIMARY CARE
    // ==========================================
    {
      id: "family_medicine",
      name: "Family Medicine",
      category: "Primary Care",
      priorityTemplates: [
        "glp1_t2dm",
        "glp1_obesity",
        "sglt2_hf",
        "mri_lumbar",
        "sleep_study",
        "biologic_ra",
        "colonoscopy",
      ],
    },
    {
      id: "internal_medicine",
      name: "Internal Medicine",
      category: "Primary Care",
      priorityTemplates: [
        "glp1_t2dm",
        "sglt2_hf",
        "glp1_obesity",
        "pcsk9",
        "mri_lumbar",
        "sleep_study",
        "colonoscopy",
      ],
    },
    {
      id: "geriatrics",
      name: "Geriatrics",
      category: "Primary Care",
      priorityTemplates: [
        "sglt2_hf",
        "pcsk9",
        "mri_lumbar",
        "dme_cpap",
        "dme_wheelchair",
        "home_health",
        "snf_admission",
      ],
    },
  
    // ==========================================
    // INTERNAL MEDICINE SUBSPECIALTIES
    // ==========================================
    {
      id: "cardiology",
      name: "Cardiology",
      category: "Internal Medicine Subspecialties",
      priorityTemplates: [
        "sglt2_hf",
        "pcsk9",
        "cardiac_cath",
        "cardiac_mri",
        "stress_test",
        "echocardiogram",
        "pacemaker",
        "ablation",
      ],
    },
    {
      id: "endocrinology",
      name: "Endocrinology",
      category: "Internal Medicine Subspecialties",
      priorityTemplates: [
        "glp1_t2dm",
        "glp1_obesity",
        "sglt2_hf",
        "insulin_pump",
        "cgm",
        "thyroid_ultrasound",
        "dxa_scan",
      ],
    },
    {
      id: "gastroenterology",
      name: "Gastroenterology",
      category: "Internal Medicine Subspecialties",
      priorityTemplates: [
        "colonoscopy",
        "egd",
        "biologic_ibd",
        "biologic_crohns",
        "ercp",
        "liver_biopsy",
        "ct_abdomen",
        "mrcp",
      ],
    },
    {
      id: "pulmonology",
      name: "Pulmonology",
      category: "Internal Medicine Subspecialties",
      priorityTemplates: [
        "sleep_study",
        "dme_cpap",
        "dme_bipap",
        "dme_oxygen",
        "ct_chest",
        "bronchoscopy",
        "pulm_rehab",
        "biologic_asthma",
      ],
    },
    {
      id: "nephrology",
      name: "Nephrology",
      category: "Internal Medicine Subspecialties",
      priorityTemplates: [
        "sglt2_hf",
        "dialysis_access",
        "kidney_biopsy",
        "renal_ultrasound",
        "transplant_eval",
        "epo_agents",
      ],
    },
    {
      id: "rheumatology",
      name: "Rheumatology",
      category: "Internal Medicine Subspecialties",
      priorityTemplates: [
        "biologic_ra",
        "biologic_psoriasis",
        "biologic_as",
        "jak_inhibitor",
        "mri_joint",
        "joint_injection",
      ],
    },
    {
      id: "oncology",
      name: "Hematology/Oncology",
      category: "Internal Medicine Subspecialties",
      priorityTemplates: [
        "chemo_regimen",
        "immunotherapy",
        "targeted_therapy",
        "pet_scan",
        "bone_marrow_biopsy",
        "genetic_testing",
        "radiation_therapy",
      ],
    },
    {
      id: "infectious_disease",
      name: "Infectious Disease",
      category: "Internal Medicine Subspecialties",
      priorityTemplates: [
        "hiv_medication",
        "hep_c_treatment",
        "iv_antibiotics",
        "picc_line",
        "outpatient_abx",
      ],
    },
    {
      id: "neurology",
      name: "Neurology",
      category: "Internal Medicine Subspecialties",
      priorityTemplates: [
        "mri_brain",
        "mri_spine",
        "eeg",
        "emg_ncv",
        "ms_medication",
        "migraine_cgrp",
        "epilepsy_med",
        "botox_migraine",
      ],
    },
    {
      id: "allergy_immunology",
      name: "Allergy/Immunology",
      category: "Internal Medicine Subspecialties",
      priorityTemplates: [
        "biologic_asthma",
        "biologic_eczema",
        "allergy_testing",
        "immunotherapy",
        "epipen",
      ],
    },
  
    // ==========================================
    // SURGICAL SPECIALTIES
    // ==========================================
    {
      id: "general_surgery",
      name: "General Surgery",
      category: "Surgical Specialties",
      priorityTemplates: [
        "bariatric_surgery",
        "hernia_repair",
        "cholecystectomy",
        "colonoscopy",
        "ct_abdomen",
      ],
    },
    {
      id: "orthopedics",
      name: "Orthopedic Surgery",
      category: "Surgical Specialties",
      priorityTemplates: [
        "mri_lumbar",
        "mri_knee",
        "mri_shoulder",
        "joint_replacement",
        "spine_surgery",
        "arthroscopy",
        "dme_brace",
        "physical_therapy",
      ],
    },
    {
      id: "neurosurgery",
      name: "Neurosurgery",
      category: "Surgical Specialties",
      priorityTemplates: [
        "mri_spine",
        "mri_brain",
        "spine_surgery",
        "craniotomy",
        "spinal_fusion",
        "dbs",
      ],
    },
    {
      id: "urology",
      name: "Urology",
      category: "Surgical Specialties",
      priorityTemplates: [
        "ct_abdomen",
        "cystoscopy",
        "prostatectomy",
        "lithotripsy",
        "urodynamics",
      ],
    },
    {
      id: "ophthalmology",
      name: "Ophthalmology",
      category: "Surgical Specialties",
      priorityTemplates: [
        "cataract_surgery",
        "retinal_injection",
        "glaucoma_surgery",
        "oct_imaging",
      ],
    },
    {
      id: "ent",
      name: "ENT/Otolaryngology",
      category: "Surgical Specialties",
      priorityTemplates: [
        "sleep_study",
        "sinus_surgery",
        "tonsillectomy",
        "ct_sinus",
        "hearing_aids",
        "cochlear_implant",
      ],
    },
    {
      id: "plastic_surgery",
      name: "Plastic Surgery",
      category: "Surgical Specialties",
      priorityTemplates: [
        "breast_reconstruction",
        "skin_graft",
        "panniculectomy",
      ],
    },
    {
      id: "vascular_surgery",
      name: "Vascular Surgery",
      category: "Surgical Specialties",
      priorityTemplates: [
        "carotid_ultrasound",
        "angiogram",
        "bypass_surgery",
        "endovascular",
      ],
    },
    {
      id: "cardiothoracic",
      name: "Cardiothoracic Surgery",
      category: "Surgical Specialties",
      priorityTemplates: [
        "cabg",
        "valve_replacement",
        "lung_resection",
        "cardiac_cath",
      ],
    },
  
    // ==========================================
    // PEDIATRIC SPECIALTIES
    // ==========================================
    {
      id: "pediatrics",
      name: "Pediatrics",
      category: "Pediatric Specialties",
      priorityTemplates: [
        "adhd_medication",
        "asthma_biologic",
        "eczema_biologic",
        "genetic_testing",
        "developmental_eval",
      ],
    },
    {
      id: "peds_cardiology",
      name: "Pediatric Cardiology",
      category: "Pediatric Specialties",
      priorityTemplates: [
        "echocardiogram",
        "cardiac_mri",
        "congenital_heart_surgery",
      ],
    },
    {
      id: "peds_neurology",
      name: "Pediatric Neurology",
      category: "Pediatric Specialties",
      priorityTemplates: [
        "eeg",
        "mri_brain",
        "epilepsy_med",
        "genetic_testing",
      ],
    },
    {
      id: "peds_pulm",
      name: "Pediatric Pulmonology",
      category: "Pediatric Specialties",
      priorityTemplates: [
        "biologic_asthma",
        "sleep_study",
        "cf_medication",
        "bronchoscopy",
      ],
    },
    {
      id: "peds_gi",
      name: "Pediatric Gastroenterology",
      category: "Pediatric Specialties",
      priorityTemplates: [
        "biologic_ibd",
        "egd",
        "colonoscopy",
      ],
    },
  
    // ==========================================
    // OTHER SPECIALTIES
    // ==========================================
    {
      id: "psychiatry",
      name: "Psychiatry",
      category: "Other Specialties",
      priorityTemplates: [
        "antipsychotic",
        "ketamine_esketamine",
        "tms",
        "ect",
        "genetic_testing_psych",
        "residential_treatment",
      ],
    },
    {
      id: "pm_r",
      name: "Physical Medicine & Rehab",
      category: "Other Specialties",
      priorityTemplates: [
        "mri_spine",
        "emg_ncv",
        "physical_therapy",
        "dme_wheelchair",
        "dme_brace",
        "inpatient_rehab",
        "botox_spasticity",
      ],
    },
    {
      id: "pain_management",
      name: "Pain Management",
      category: "Other Specialties",
      priorityTemplates: [
        "mri_spine",
        "epidural_injection",
        "spinal_cord_stim",
        "nerve_block",
        "rfa",
        "intrathecal_pump",
      ],
    },
    {
      id: "dermatology",
      name: "Dermatology",
      category: "Other Specialties",
      priorityTemplates: [
        "biologic_psoriasis",
        "biologic_eczema",
        "mohs_surgery",
        "phototherapy",
        "acne_isotretinoin",
      ],
    },
    {
      id: "obgyn",
      name: "OB/GYN",
      category: "Other Specialties",
      priorityTemplates: [
        "hysterectomy",
        "pelvic_ultrasound",
        "mri_pelvis",
        "fertility_treatment",
        "genetic_testing",
      ],
    },
    {
      id: "maternal_fetal",
      name: "Maternal-Fetal Medicine",
      category: "Other Specialties",
      priorityTemplates: [
        "fetal_mri",
        "amniocentesis",
        "genetic_testing",
        "high_risk_monitoring",
      ],
    },
    {
      id: "sleep_medicine",
      name: "Sleep Medicine",
      category: "Other Specialties",
      priorityTemplates: [
        "sleep_study",
        "dme_cpap",
        "dme_bipap",
        "dme_oral_appliance",
        "inspire_implant",
      ],
    },
    {
      id: "sports_medicine",
      name: "Sports Medicine",
      category: "Other Specialties",
      priorityTemplates: [
        "mri_knee",
        "mri_shoulder",
        "physical_therapy",
        "prp_injection",
        "arthroscopy",
      ],
    },
    {
      id: "hospice_palliative",
      name: "Hospice & Palliative Care",
      category: "Other Specialties",
      priorityTemplates: [
        "hospice_admission",
        "dme_hospital_bed",
        "dme_oxygen",
        "pain_medication",
      ],
    },
    {
      id: "wound_care",
      name: "Wound Care",
      category: "Other Specialties",
      priorityTemplates: [
        "hyperbaric_oxygen",
        "wound_vac",
        "skin_substitute",
        "debridement",
      ],
    },
  ];
  
  // ==========================================
  // HELPER FUNCTIONS
  // ==========================================
  
  /**
   * Get specialty by ID
   */
  export function getSpecialtyById(id: string): Specialty | undefined {
    return SPECIALTIES.find((s) => s.id === id);
  }
  
  /**
   * Get specialties grouped by category
   */
  export function getSpecialtiesByCategory(): Record<string, Specialty[]> {
    const grouped: Record<string, Specialty[]> = {};
    
    SPECIALTY_CATEGORIES.forEach((category) => {
      grouped[category] = SPECIALTIES.filter((s) => s.category === category);
    });
    
    return grouped;
  }
  
  /**
   * Given a list of specialty IDs, return the combined priority template list
   * Templates are weighted by how many specialties include them
   */
  export function getPriorityTemplatesForSpecialties(
    specialtyIds: string[]
  ): string[] {
    if (!specialtyIds || specialtyIds.length === 0) return [];
  
    // Count how often each template appears across selected specialties
    const templateScores: Record<string, number> = {};
  
    specialtyIds.forEach((specialtyId) => {
      const specialty = getSpecialtyById(specialtyId);
      if (!specialty) return;
  
      specialty.priorityTemplates.forEach((templateId, index) => {
        // Higher score for templates that appear earlier in priority list
        // and for templates that appear in multiple specialties
        const positionScore = specialty.priorityTemplates.length - index;
        templateScores[templateId] = (templateScores[templateId] || 0) + positionScore;
      });
    });
  
    // Sort by score (descending) and return template IDs
    return Object.entries(templateScores)
      .sort((a, b) => b[1] - a[1])
      .map(([templateId]) => templateId);
  }
  
  /**
   * Reorder templates based on physician's specialties
   */
  export function reorderTemplatesBySpecialty<T extends { id: string }>(
    templates: T[],
    specialtyIds: string[]
  ): T[] {
    const priorityTemplateIds = getPriorityTemplatesForSpecialties(specialtyIds);
  
    if (priorityTemplateIds.length === 0) return templates;
  
    // Create a priority map for O(1) lookup
    const priorityMap = new Map<string, number>();
    priorityTemplateIds.forEach((id, index) => {
      priorityMap.set(id, index);
    });
  
    // Sort templates: priority templates first (in order), then the rest
    return [...templates].sort((a, b) => {
      const aPriority = priorityMap.has(a.id) ? priorityMap.get(a.id)! : Infinity;
      const bPriority = priorityMap.has(b.id) ? priorityMap.get(b.id)! : Infinity;
      return aPriority - bPriority;
    });
  }